import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { MoneyInput } from '@/components/ui/money-input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { formatTail } from '@/lib/format.js';
import { ApiError } from '@/lib/api.js';
import { updateAircraftCoverage, getAirportRunway, stateAirportRunway } from '@/services/deals.service.js';
import { aircraftLabel } from '@/lib/aircraftLabel.js';

// Per-term coverage editor for one aircraft
// (AIRCRAFT-CANONICALIZATION-PHASE1-SPEC 1c).
//
// Writes through PATCH .../aircraft/:tail/coverage → policy-aircraft.service,
// the single writer: the Supabase row is the commit, the CRM AoP mirror is
// non-blocking and comes back as `warnings`. A warning is surfaced as an info
// toast, NOT an error — the value did save, the mirror just lagged.
//
// SPARSE BY CONSTRUCTION: only fields the broker actually changed are sent, so
// this dialog can never null out a field it didn't touch (the Got Wings class
// of bug, where an editor's empty state landed $0 deductibles on a bound row).

const USES = ['Pleasure & Business', 'Business & Pleasure', 'Commercial', 'Instruction', 'Rental', 'Charter'];

// '' and null both mean "no value" for diffing; anything else compares as text.
const same = (a, b) => String(a ?? '') === String(b ?? '');

const inputCls =
  'w-full rounded-[5px] border border-border bg-surface px-2.5 py-1.5 text-sm text-text outline-none focus:border-border-strong';
const labelCls = 'mb-1 block text-xs text-text-muted';

export default function AircraftCoverageDialog({ dealId, aircraft, bases, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  // THE RUNWAY at the saved base airport, as the carrier builders get it (the
  // one resolver: OurAirports, then the stated or cached answer, then IAT).
  // Read, never this row's own runway copy. { state: 'idle'|'loading'|'ready'|'error', row, surfaces }
  const [runway, setRunway] = useState({ state: 'idle', row: null, surfaces: [] });
  const [stated, setStated] = useState({ length: '', surface: '' });
  const [stating, setStating] = useState(false);
  const savedAirport = aircraft?.base_airport || '';
  useEffect(() => {
    let live = true;
    setStated({ length: '', surface: '' });
    if (!open || !savedAirport) { setRunway({ state: 'idle', row: null, surfaces: [] }); return undefined; }
    setRunway((r) => ({ ...r, state: 'loading' }));
    getAirportRunway(savedAirport)
      .then((res) => { if (live) setRunway({ state: 'ready', row: res.runway || null, surfaces: res.surfaces || [] }); })
      .catch(() => { if (live) setRunway({ state: 'error', row: null, surfaces: [] }); });
    return () => { live = false; };
  }, [open, savedAirport]);

  // Re-seed whenever a different aircraft is opened so the form never shows
  // the previous row's values for a moment.
  useEffect(() => {
    if (!aircraft) return setForm(null);
    setForm({
      hull_value: aircraft.hull_value ?? '',
      coverage_basis: aircraft.coverage_basis ?? '',
      deductible_in_motion: aircraft.deductible_in_motion ?? '',
      deductible_not_in_motion: aircraft.deductible_not_in_motion ?? '',
      base_airport: aircraft.base_airport ?? '',
      aircraft_use: aircraft.aircraft_use ?? '',
    });
  }, [aircraft]);

  if (!aircraft || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Only what changed. Empty string → null so "cleared" is distinguishable
  // from "untouched" server-side (zero stays zero — it is a value, not a hole).
  const buildPatch = () => {
    const patch = {};
    const original = {
      hull_value: aircraft.hull_value ?? '',
      coverage_basis: aircraft.coverage_basis ?? '',
      deductible_in_motion: aircraft.deductible_in_motion ?? '',
      deductible_not_in_motion: aircraft.deductible_not_in_motion ?? '',
      base_airport: aircraft.base_airport ?? '',
      aircraft_use: aircraft.aircraft_use ?? '',
    };
    for (const k of Object.keys(original)) {
      if (same(form[k], original[k])) continue;
      patch[k] = form[k] === '' ? null : form[k];
    }
    return patch;
  };

  const dirty = Object.keys(buildPatch()).length > 0;

  const stateRunway = async () => {
    setStating(true);
    try {
      const res = await stateAirportRunway(savedAirport, { runway_length_ft: Number(stated.length), runway_surface: stated.surface });
      const r = res.runway || {};
      toast.success(`${savedAirport} runway saved: ${r.runway_length_ft} ft, ${r.runway_surface}`);
      // A confirmation is a read: re-ask the resolver the carrier builders ask.
      const back = await getAirportRunway(savedAirport);
      setRunway({ state: 'ready', row: back.runway || null, surfaces: back.surfaces || [] });
      if (onSaved) await onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message;
      toast.error(`Runway not saved: ${msg}`);
    } finally {
      setStating(false);
    }
  };
  const SOURCE_WORDS = { ourairports: 'the airport data', iat: 'IAT', broker: 'stated by' };

  const save = async () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) return onOpenChange(false);
    setBusy(true);
    try {
      const res = await updateAircraftCoverage(dealId, aircraft.tail_number, patch);
      toast.success(`${formatTail(aircraft.tail_number)} coverage saved`);
      // The canonical write succeeded; a mirror warning is worth seeing but is
      // not a failure. Say which one it was rather than a generic "warning".
      for (const w of res.warnings || []) toast.info(`${formatTail(aircraft.tail_number)}: ${w}`);
      onOpenChange(false);
      if (onSaved) await onSaved();
    } catch (err) {
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message;
      toast.error(`Save failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  // needsBasis removed with the Fleet card's NO BASIS badge (prompt 73). Both
  // warned that a hull value with no basis would make a carrier drop the hull,
  // which is a prediction from one deal and one carrier and was falsified on
  // N8095M. And the blank it detected was a copy failure, not a missing answer:
  // the client states the basis at intake and nothing wrote it down.

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Coverage · {formatTail(aircraft.tail_number)}</DialogTitle>
          <DialogDescription>
            {aircraftLabel(aircraft, 'This term')}.
            Saves to the canonical record the carrier request reads, then mirrors to CRM.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls} htmlFor="hull">Hull value</label>
            <MoneyInput
              id="hull"
              value={form.hull_value}
              onValueChange={(v) => set('hull_value', v.floatValue ?? '')}
              placeholder="$ 0"
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls} htmlFor="basis">Physical damage basis</label>
            <select
              id="basis"
              value={form.coverage_basis}
              onChange={(e) => set('coverage_basis', e.target.value)}
              className={inputCls}
            >
              <option value="">— not set —</option>
              {(bases || []).map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls} htmlFor="dim">Deductible · in motion</label>
            {/* Free text, not a money input: percentage deductibles ("2%",
                "1% of hull") are real and must pass through verbatim. */}
            <input
              id="dim"
              type="text"
              value={form.deductible_in_motion}
              onChange={(e) => set('deductible_in_motion', e.target.value)}
              placeholder="$1,000 or 2%"
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls} htmlFor="dnim">Deductible · not in motion</label>
            <input
              id="dnim"
              type="text"
              value={form.deductible_not_in_motion}
              onChange={(e) => set('deductible_not_in_motion', e.target.value)}
              placeholder="$500 or 1%"
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls} htmlFor="apt">Base airport</label>
            <input
              id="apt"
              type="text"
              value={form.base_airport}
              onChange={(e) => set('base_airport', e.target.value.toUpperCase())}
              placeholder="KPGD"
              maxLength={4}
              className={`${inputCls} font-mono uppercase`}
            />
            {runway.state === 'loading' ? (
              <div className="mt-1 text-[11px] text-text-dim">Checking the runway…</div>
            ) : runway.state === 'error' ? (
              <div className="mt-1 text-[11px] text-warning">Could not check the runway for {savedAirport}.</div>
            ) : runway.row?.not_an_identifier ? (
              <div className="mt-1 text-[11px] text-warning" data-runway="not-an-identifier">{savedAirport} is not an airport identifier. Set the FAA identifier.</div>
            ) : runway.row?.runway_length ? (
              <div className="mt-1 text-[11px] text-text-dim" data-runway="answered">
                {[`${Number(runway.row.runway_length).toLocaleString('en-US')} ft`, runway.row.runway_surface].filter(Boolean).join(' · ')}
                {' · '}from {SOURCE_WORDS[runway.row.runway_source] || runway.row.runway_source}{runway.row.runway_source === 'broker' && runway.row.stated_by ? ` ${runway.row.stated_by}` : ''}
              </div>
            ) : null}
          </div>

          {runway.state === 'ready' && runway.row && !runway.row.not_an_identifier && !runway.row.runway_length ? (
            <div className="col-span-2 rounded-[5px] border border-border bg-surface-strong p-2.5" data-runway="state-it">
              <div className="mb-1.5 text-xs text-text">
                No runway for {savedAirport} in the airport data{runway.row.unchecked?.length ? ` (could not check ${runway.row.unchecked.join(', ')})` : ' or from IAT'}. The carriers need its length and surface.
              </div>
              <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                <div>
                  <label className={labelCls} htmlFor="rwylen">Runway length (ft)</label>
                  <input id="rwylen" type="number" inputMode="numeric" min={100} max={20000} step={1} value={stated.length}
                    onChange={(e) => setStated((v) => ({ ...v, length: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="rwysurf">Surface</label>
                  <select id="rwysurf" value={stated.surface} onChange={(e) => setStated((v) => ({ ...v, surface: e.target.value }))} className={inputCls}>
                    <option value="">Choose</option>
                    {runway.surfaces.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <button type="button" disabled={stating || !stated.length || !stated.surface} onClick={stateRunway}
                  className="inline-flex items-center rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50">
                  {stating ? 'Saving…' : 'Save runway'}
                </button>
              </div>
            </div>
          ) : null}

          <div>
            <label className={labelCls} htmlFor="use">Aircraft use</label>
            <select
              id="use"
              value={form.aircraft_use}
              onChange={(e) => set('aircraft_use', e.target.value)}
              className={inputCls}
            >
              <option value="">— not set —</option>
              {USES.map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            disabled={busy}
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1.5 rounded-[5px] border border-border bg-surface px-3 py-1.5 text-[12.5px] text-text hover:border-border-strong hover:bg-surface-strong disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || !dirty}
            onClick={save}
            className="inline-flex items-center gap-1.5 rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Save coverage'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
